import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut, Settings, Package, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { settingsAPI } from '../../services/api';

// Helper function to determine if a color is light or dark
function isLightColor(color) {
  if (!color) return true;
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
}

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerSettings, setBannerSettings] = useState({
    enabled: true,
    text: 'Free UK Delivery on Orders Over £100',
    color: '#f59e0b'
  });
  const { cartCount, setIsCartOpen } = useCart();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const productCategories = [
    { name: 'Injectable Oils', slug: 'injectable-oils' },
    { name: 'Oral Compounds', slug: 'oral-compounds' },
    { name: 'PCT & Support', slug: 'pct-support' },
    { name: 'Peptides & GLP-1', slug: 'peptides' },
    { name: 'Weight Loss', slug: 'weight-loss' },
    { name: 'Health & Wellness', slug: 'health-wellness' },
    { name: 'Sexual Health', slug: 'sexual-health' },
    { name: 'Tanning', slug: 'tanning' },
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setBannerSettings({
          enabled: response.data.bannerEnabled !== false,
          text: response.data.bannerText || 'Free UK Delivery on Orders Over £100',
          color: response.data.bannerColor || '#f59e0b'
        });
      } catch (error) {
        // Use default banner settings
      }
    };
    fetchSettings();
  }, []);

  const textColor = isLightColor(bannerSettings.color) ? '#000' : '#fff';

  return (
    <>
      {/* Top announcement bar */}
      {bannerSettings.enabled && (
        <div 
          className="py-2 text-sm overflow-hidden"
          style={{ backgroundColor: bannerSettings.color, color: textColor }}
        >
          <div className="animate-marquee whitespace-nowrap flex">
            <span className="mx-8 font-medium">🔥 {bannerSettings.text}</span>
            <span className="mx-8 font-medium">🔥 {bannerSettings.text}</span>
            <span className="mx-8 font-medium">🔥 {bannerSettings.text}</span>
            <span className="mx-8 font-medium">🔥 {bannerSettings.text}</span>
          </div>
        </div>
      )}

      {/* Main header */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo.png" 
                alt="ForgedSupps" 
                className="h-10 sm:h-12 md:h-14 w-auto object-contain max-w-[180px] sm:max-w-[220px]"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-zinc-300 hover:text-amber-500 transition-colors font-medium"
              >
                Home
              </Link>
              
              {/* Products Dropdown - pure hover */}
              <div className="relative group">
                <Link
                  to="/products"
                  className="flex items-center gap-1 text-zinc-300 hover:text-amber-500 transition-colors font-medium py-4"
                >
                  Products
                  <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                </Link>
                <div className="absolute top-full left-0 w-52 bg-zinc-900 border border-zinc-800 rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-1">
                    <Link
                      to="/products"
                      className="block px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 font-medium"
                    >
                      All Products
                    </Link>
                    <div className="h-px bg-zinc-800 mx-2" />
                    {productCategories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/products?category=${category.slug}`}
                        className="block px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800"
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/contact"
                className="text-zinc-300 hover:text-amber-500 transition-colors font-medium"
              >
                Contact
              </Link>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-4">
              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-zinc-300 hover:text-amber-500">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                  {isAuthenticated ? (
                    <>
                      <div className="px-3 py-2">
                        <p className="text-sm text-white font-medium">{user.name}</p>
                        <p className="text-xs text-zinc-400">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/account')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      >
                        <Package className="mr-2 h-4 w-4" />
                        My Orders
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Panel
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem 
                        onClick={logout}
                        className="text-red-400 hover:text-red-300 hover:bg-zinc-800 cursor-pointer"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem 
                        onClick={() => navigate('/login')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      >
                        Login
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/register')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      >
                        Register
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cart button */}
              <Button
                variant="ghost"
                size="icon"
                className="text-zinc-300 hover:text-amber-500 relative"
                onClick={() => setIsCartOpen(true)}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-zinc-300"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-t border-zinc-800">
            <nav className="px-4 py-4 space-y-1">
              <Link
                to="/"
                className="block py-2 text-zinc-300 hover:text-amber-500 transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              
              {/* Products section */}
              <div className="py-2">
                <Link
                  to="/products"
                  className="block text-zinc-300 hover:text-amber-500 transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Products
                </Link>
                <div className="pl-4 mt-2 space-y-1 border-l border-zinc-800">
                  {productCategories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/products?category=${category.slug}`}
                      className="block py-1.5 text-zinc-400 hover:text-amber-500 transition-colors text-sm"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="block py-2 text-zinc-300 hover:text-amber-500 transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
